import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { peopleService } from '../../services/peopleService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import { cell, GRID } from '../../styles/gridStyles';

// =============================================================================
// Grades — salary-band reference per grade, wired to bud_grades. Low / Mid /
// Max monthly range (encrypted at rest) plus allowed annual leave. Person reads
// its band from here. Scoped to the budget version in the top bar.
// =============================================================================

const inputClass =
  'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

const spreadPct = (g) => (g.mid ? Math.round(((g.max - g.low) / g.mid) * 100) : 0);

const AddGradeModal = ({ existing, onAdd, onClose }) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const c = code.trim();
  const dup = c && existing.has(c);
  const problem = !c ? 'A grade code is required.' : dup ? 'That grade already exists.' : !description.trim() ? 'A description is required.' : '';
  const canAdd = !problem;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-center justify-between">
          <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add grade</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Grade code <span className="text-red-600 dark:text-red-400">(required)</span>
            <input className={inputClass} value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. C1" autoFocus />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Description <span className="text-red-600 dark:text-red-400">(required)</span>
            <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Supervisory band" />
          </label>
          <p className="m-0 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem}</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">Cancel</button>
            <button type="button" disabled={!canAdd} onClick={() => onAdd({ grade_code: c, description: description.trim() })}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white ${canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'}`}>
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EDITABLE_NUM = ['low', 'mid', 'max', 'leave_days'];

const Grades = () => {
  const { selectedVersion } = useAppContext();
  const [grades, setGrades] = useState([]);   // [{ grade_code, description, leave_days, low, mid, max, isNew }]
  const [dirty, setDirty] = useState({});     // { `${code}`: true } — whole-row dirty (grades upsert is full-row)
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedVersion) { setGrades([]); return; }
    setIsLoading(true); setError(null);
    try {
      const res = await peopleService.getGrades();
      if (!res.success) throw new Error(res.message);
      setGrades(res.data.map((g) => ({ ...g, isNew: false })));
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load grades.');
      setGrades([]);
    } finally { setIsLoading(false); }
  }, [selectedVersion]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns = useMemo(() => ([
    { columnId: 'grade', width: 90, resizable: true },
    { columnId: 'description', width: 220, resizable: true },
    { columnId: 'low', width: 110, resizable: true },
    { columnId: 'mid', width: 110, resizable: true },
    { columnId: 'max', width: 110, resizable: true },
    { columnId: 'spread', width: 90, resizable: true },
    { columnId: 'leave_days', width: 110, resizable: true },
    { columnId: 'del', width: 40, resizable: false },
  ]), []);

  const gridRows = useMemo(() => {
    const header = {
      rowId: 'header', height: GRID.size.headerRow,
      cells: [
        cell.header('Grade'), cell.header('Description'),
        cell.header('Low', { align: 'right' }), cell.header('Mid', { align: 'right' }), cell.header('Max', { align: 'right' }),
        cell.header('Spread %', { align: 'right' }), cell.header('Allowed Leave', { align: 'right' }), cell.header(''),
      ],
    };
    const rows = grades.map((g, idx) => ({
      rowId: g.grade_code, height: GRID.size.dataRow,
      cells: [
        cell.label(g.grade_code, { idx }),
        cell.editText(g.description, { idx }),
        cell.number(g.low, { editable: true, idx }),
        cell.number(g.mid, { editable: true, idx }),
        cell.number(g.max, { editable: true, idx }),
        cell.number(spreadPct(g), { idx }),
        cell.number(g.leave_days, { editable: true, idx }),
        cell.text('✕', { idx, color: GRID.color.muted }),
      ],
    }));
    return [header, ...rows];
  }, [grades]);

  const handleChanges = (changes) => {
    setGrades((prev) => {
      const next = prev.map((g) => ({ ...g }));
      // A multi-cell paste arrives as one onCellsChanged call PER CELL in a
      // single tick. Collect this call's keys locally and merge them with a
      // functional setDirty — seeding from the `dirty` closure instead would
      // restart from the same stale base each call and only the last pasted
      // cell would survive to be saved.
      const touched = {};
      for (const ch of changes) {
        const col = String(ch.columnId);
        const g = next.find((x) => x.grade_code === ch.rowId);
        if (!g) continue;
        if (col === 'description') { g.description = ch.newCell?.text ?? ''; touched[g.grade_code] = true; continue; }
        if (!EDITABLE_NUM.includes(col)) continue;
        const val = Number(ch.newCell?.value);
        if (isNaN(val)) continue;
        g[col] = val;
        touched[g.grade_code] = true;
      }
      setDirty((prevDirty) => ({ ...prevDirty, ...touched }));
      return next;
    });
  };

  const handleFocus = (loc) => {
    if (String(loc.columnId) !== 'del') return true;
    const code = String(loc.rowId);
    if (code === 'header') return true;
    deleteGrade(code);
    return false;
  };

  const existingCodes = useMemo(() => new Set(grades.map((g) => g.grade_code)), [grades]);

  const addGrade = ({ grade_code, description }) => {
    setGrades((prev) => [...prev, { grade_code, description, leave_days: 0, low: 0, mid: 0, max: 0, isNew: true }]);
    setDirty((prev) => ({ ...prev, [grade_code]: true }));
    setAddOpen(false);
  };

  const deleteGrade = async (code) => {
    const g = grades.find((x) => x.grade_code === code);
    if (g?.isNew) {
      setGrades((prev) => prev.filter((x) => x.grade_code !== code));
      setDirty((prev) => { const d = { ...prev }; delete d[code]; return d; });
      return;
    }
    if (!window.confirm(`Delete grade ${code}?`)) return;
    setError(null);
    try { await peopleService.deleteGrade(code); await loadData(); }
    catch (e) { setError(e.response?.data?.message || 'Failed to delete grade.'); }
  };

  const hasChanges = Object.keys(dirty).length > 0;

  const handleSave = async () => {
    setError(null);
    const payload = grades
      .filter((g) => dirty[g.grade_code])
      .map((g) => ({ grade_code: g.grade_code, description: g.description, leave_days: g.leave_days, low: g.low, mid: g.mid, max: g.max }));
    if (payload.length === 0) return;
    setIsSaving(true);
    try {
      const res = await peopleService.saveGrades(payload);
      if (!res.success) throw new Error(res.message);
      await loadData();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save grades.');
    } finally { setIsSaving(false); }
  };

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Grades</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Salary-band reference per grade — Low / Mid / Max monthly range and allowed annual leave. Ranges are encrypted at rest. Version comes from the top bar.
        </p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!selectedVersion ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : (
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              onFocusLocationChanging={handleFocus}
              stickyBottomRows={0}
              hint="Monthly ranges in R. Spread % = (Max − Low) ÷ Mid. ✕ deletes a grade."
              toolbar={
                <>
                  <MantleGridButton onClick={() => setAddOpen(true)}>+ Add grade</MantleGridButton>
                  <MantleGridButton onClick={handleSave} disabled={isSaving || !hasChanges} accent={hasChanges}>{isSaving ? 'Saving…' : 'Save'}</MantleGridButton>
                </>
              }
            />
          )}
        </div>
      </div>

      {addOpen && <AddGradeModal existing={existingCodes} onAdd={addGrade} onClose={() => setAddOpen(false)} />}
    </div>
  );
};

export default Grades;
